import { useEffect, useState } from 'react';
import { CheckCircle2, Copy } from 'lucide-react';
import QRCode from 'qrcode';
import type { Device } from '@findmy/shared';
import { useCreatePairingCode } from '../api/queries';
import { useNow } from '../lib/useNow';
import { Button, ErrorText, Spinner } from './ui';

function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Mounted while the "Add device" dialog is open: creates a one-time code, shows it as
 * text and a QR code, and notices when a new device appears (paired with it).
 */
export function PairingDialogContent({ devices, onDone }: { devices: Device[]; onDone: () => void }) {
  const { mutate, data, error, isPending } = useCreatePairingCode();
  const [knownIds] = useState(() => new Set(devices.map((d) => d.id)));
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const now = useNow(1000);

  useEffect(() => mutate(), [mutate]);

  useEffect(() => {
    if (!data) return;
    let cancelled = false;
    void QRCode.toDataURL(data.qrPayload, { margin: 1, width: 240, errorCorrectionLevel: 'M' }).then(
      (url) => {
        if (!cancelled) setQr(url);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [data]);

  const paired = devices.find((d) => !knownIds.has(d.id));
  if (paired) {
    return (
      <div className="space-y-4 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" aria-hidden />
        <p className="text-lg font-medium">{paired.name} is paired</p>
        <p className="text-sm text-slate-600 dark:text-slate-400">
          It will appear on the map once it reports its location.
        </p>
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      </div>
    );
  }

  if (error) return <ErrorText>{error.message}</ErrorText>;
  if (isPending || !data) return <Spinner label="Creating a pairing code…" />;

  const remaining = Date.parse(data.expiresAt) - now;
  const command = `findmy-agent pair ${window.location.origin} ${data.code}`;

  if (remaining <= 0) {
    return (
      <div className="space-y-4">
        <p className="text-sm">This code has expired. Codes last 10 minutes and work once.</p>
        <Button variant="primary" onClick={() => mutate()}>
          Create a new code
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-600 dark:text-slate-400">
        On the device you want to add, open Find My and enter this code or scan the QR code. Pairing has to be
        done on the device itself.
      </p>

      <div className="flex flex-col items-center gap-3">
        <p
          className="font-mono text-4xl font-semibold tracking-[0.2em]"
          data-testid="pairing-code"
          aria-label={`Pairing code ${data.code.split('').join(' ')}`}
        >
          {data.code.slice(0, 3)} {data.code.slice(3)}
        </p>
        <p className="text-xs text-slate-500" aria-live="polite">
          Expires in {formatCountdown(remaining)}
        </p>
        {qr && (
          <img
            src={qr}
            alt="QR code for pairing"
            width={200}
            height={200}
            className="rounded-lg bg-white p-2"
          />
        )}
      </div>

      <div className="space-y-1">
        <p className="text-xs font-medium text-slate-600 dark:text-slate-400">On a Linux PC, run:</p>
        <div className="flex items-center gap-2 rounded-lg bg-slate-100 p-2 dark:bg-slate-800">
          <code className="min-w-0 flex-1 truncate text-xs">{command}</code>
          <button
            type="button"
            onClick={() => void navigator.clipboard.writeText(command).then(() => setCopied(true))}
            className="shrink-0 rounded p-1 hover:bg-slate-200 dark:hover:bg-slate-700"
            aria-label="Copy command"
            title={copied ? 'Copied' : 'Copy'}
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
      </div>

      <p className="flex items-center gap-2 text-xs text-slate-500">
        <span className="h-2 w-2 animate-pulse rounded-full bg-blue-500" aria-hidden /> Waiting for the
        device…
      </p>
    </div>
  );
}
